import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ratesAPI, ecommerceAPI, sourcingAPI, exchangeRateAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { 
  Ship, Package, Warehouse, ArrowRight, CheckCircle, Globe, 
  Zap, Shield, Clock, TrendingUp, Users, Star, ChevronRight,
  Plane, Truck, CreditCard, HeadphonesIcon, Play, RefreshCw,
  Store, ShoppingCart, Eye, Plus, Minus, Trash2, X, Loader2,
  ChevronDown, Filter, Search, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import AutoCarousel from '../components/AutoCarousel';
import HeroSourcingSearch from '../components/HeroSourcingSearch';

// Animated counter component
function AnimatedCounter({ end, duration = 2000, suffix = '', decimals = 0 }) {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const value = progress * end;
      setCount(decimals > 0 ? value.toFixed(decimals) : Math.floor(value));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration, decimals]);
  
  return <span>{typeof count === 'number' ? count.toLocaleString() : count}{suffix}</span>;
}

// Floating RMB Rate Widget — sticky with smooth glow animation
function FloatingRateWidget({ rates, loading }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (loading || !rates) return null;
  
  return (
    <div 
      className="fixed bottom-20 sm:bottom-6 right-4 z-50 cursor-pointer landing-rate-widget"
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className={`
        bg-gradient-to-br from-primary to-primary/90 text-white rounded-2xl shadow-2xl shadow-primary/30 
        transition-all duration-300 ease-out overflow-hidden
        ${isExpanded ? 'w-64' : 'w-auto'}
      `}>
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-white/70">Exchange Rate</p>
              <p className="text-xl font-black">₦{rates.ngn_to_rmb}/¥</p>
            </div>
          </div>
          
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-white/20 space-y-3 animate-fade-in">
              <div className="flex justify-between text-sm">
                <span className="text-white/70">1 RMB</span>
                <span className="font-bold">= ₦{rates.ngn_to_rmb}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">USD Ref.</span>
                <span className="font-bold">₦{rates.usd_reference}</span>
              </div>
              <div className="text-xs text-white/50 text-center pt-2">
                Updated: {rates.updated_at ? new Date(rates.updated_at).toLocaleDateString() : 'Live'}
              </div>
              <Link to="/register" className="block">
                <Button size="sm" className="w-full bg-white/20 hover:bg-white/30 text-white text-xs mt-2">
                  Start Trading
                </Button>
              </Link>
            </div>
          )}
        </div>
        {!isExpanded && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
        )}
      </div>
    </div>
  );
}

// Scroll reveal hook using Intersection Observer
function useScrollReveal() {
  const ref = useRef(null);
  
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add('revealed');
          observer.unobserve(node);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  
  return ref;
}

// Reusable scroll-reveal section wrapper
function RevealSection({ children, className = '', delay = 0 }) {
  const ref = useScrollReveal();
  return (
    <div 
      ref={ref} 
      className={`scroll-reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

// Guest cart helpers (localStorage-backed)
const GUEST_CART_KEY = 'sinolink_guest_cart';
function getGuestCart() {
  try { return JSON.parse(localStorage.getItem(GUEST_CART_KEY)) || []; } catch { return []; }
}
function saveGuestCart(items) {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
}

// Full interactive product card for guest storefront
function StoreProductCard({ product, guestCart, setGuestCart, onViewDetail, exchangeRate }) {
  const cartItem = guestCart.find(i => i._id === product._id);
  const qty = cartItem?.qty || 0;

  const addToCart = (e) => {
    e.stopPropagation();
    const updated = qty > 0
      ? guestCart.map(i => i._id === product._id ? { ...i, qty: i.qty + 1 } : i)
      : [...guestCart, { _id: product._id, name: product.name, price_rmb: product.price_rmb, original_price_rmb: product.original_price_rmb, image: product.images?.[0] || '', qty: 1 }];
    setGuestCart(updated);
    saveGuestCart(updated);
    toast.success(`${product.name.slice(0, 30)} added to cart`);
  };

  const discount = product.original_price_rmb && product.original_price_rmb > product.price_rmb
    ? Math.round((1 - product.price_rmb / product.original_price_rmb) * 100) : 0;

  const ngnPrice = exchangeRate ? (product.price_rmb * exchangeRate).toLocaleString(undefined, { maximumFractionDigits: 0 }) : null;

  return (
    <div className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-primary/30 hover:shadow-xl transition-all duration-300" data-testid={`store-product-${product._id}`}>
      <div onClick={() => onViewDetail(product)} className="cursor-pointer">
        <div className="aspect-square bg-slate-50 relative overflow-hidden">
          {product.images?.[0] ? (
            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-50">
              <Package className="h-10 w-10 text-slate-300" />
            </div>
          )}
          {discount > 0 && (
            <div className="absolute top-2 left-2 bg-primary text-white text-[10px] px-2 py-0.5 rounded-md font-bold">-{discount}%</div>
          )}
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <span className="bg-white text-primary px-3 py-1.5 rounded-lg text-xs font-bold translate-y-3 group-hover:translate-y-0 transition-transform">
              <Eye className="inline h-3.5 w-3.5 mr-1" /> Quick View
            </span>
          </div>
        </div>
        <div className="px-3 pt-3">
          <p className="text-sm font-medium text-slate-800 line-clamp-2 min-h-[40px] group-hover:text-primary transition-colors">{product.name}</p>
          <div className="mt-1.5 flex items-end justify-between">
            <div>
              <p className="text-lg font-bold text-primary">¥{product.price_rmb?.toLocaleString()}</p>
              {ngnPrice && <p className="text-[11px] text-slate-500">≈ ₦{ngnPrice}</p>}
              {discount > 0 && <p className="text-xs text-slate-400 line-through">¥{product.original_price_rmb?.toLocaleString()}</p>}
            </div>
            {product.rating && (
              <div className="flex items-center gap-0.5 bg-amber-50 px-1.5 py-0.5 rounded">
                <Star className="h-3 w-3 text-amber-500 fill-current" /><span className="text-[10px] font-medium text-amber-700">{product.rating}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="px-3 pb-3 pt-2">
        {qty > 0 ? (
          <div className="flex items-center justify-between bg-primary/10 rounded-lg p-1">
            <span className="text-xs text-primary font-semibold px-2">In Cart ({qty})</span>
            <button onClick={addToCart} className="h-7 px-3 bg-primary text-white text-xs font-bold rounded-md hover:bg-primary/90 transition-colors">+ Add</button>
          </div>
        ) : (
          <button onClick={addToCart} className="w-full py-2 bg-primary/10 text-primary rounded-lg text-xs font-semibold hover:bg-primary hover:text-white transition-all flex items-center justify-center gap-1.5">
            <ShoppingCart className="h-3.5 w-3.5" /> Add to Cart
          </button>
        )}
      </div>
    </div>
  );
}

// Product detail modal for guest viewing
function ProductDetailModal({ product, open, onClose, guestCart, setGuestCart, exchangeRate }) {
  if (!product) return null;
  const cartItem = guestCart.find(i => i._id === product._id);
  const qty = cartItem?.qty || 0;
  const ngnPrice = exchangeRate ? (product.price_rmb * exchangeRate).toLocaleString(undefined, { maximumFractionDigits: 0 }) : null;
  const discount = product.original_price_rmb && product.original_price_rmb > product.price_rmb
    ? Math.round((1 - product.price_rmb / product.original_price_rmb) * 100) : 0;

  const updateQty = (newQty) => {
    if (newQty <= 0) {
      const updated = guestCart.filter(i => i._id !== product._id);
      setGuestCart(updated); saveGuestCart(updated);
    } else if (qty === 0) {
      const updated = [...guestCart, { _id: product._id, name: product.name, price_rmb: product.price_rmb, original_price_rmb: product.original_price_rmb, image: product.images?.[0] || '', qty: newQty }];
      setGuestCart(updated); saveGuestCart(updated);
    } else {
      const updated = guestCart.map(i => i._id === product._id ? { ...i, qty: newQty } : i);
      setGuestCart(updated); saveGuestCart(updated);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
        <div className="relative">
          {product.images?.[0] && (
            <div className="aspect-video bg-slate-100 overflow-hidden"><img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" /></div>
          )}
          {discount > 0 && <span className="absolute top-3 left-3 bg-primary text-white text-sm font-bold px-3 py-1 rounded-full">-{discount}%</span>}
        </div>
        <div className="p-6 space-y-4">
          <h2 className="text-xl font-bold text-slate-900">{product.name}</h2>
          <div className="bg-orange-50 rounded-xl p-4">
            <div className="flex items-baseline gap-3"><span className="text-3xl font-bold text-primary">¥{product.price_rmb?.toFixed(2)}</span>
              {discount > 0 && <span className="text-lg text-slate-400 line-through">¥{product.original_price_rmb?.toFixed(2)}</span>}
            </div>
            {ngnPrice && <p className="text-sm text-slate-600 mt-1">≈ ₦{ngnPrice} <span className="text-xs text-slate-400">(Rate: ₦{exchangeRate}/¥1)</span></p>}
          </div>
          {product.description && <p className="text-sm text-slate-600 leading-relaxed">{product.description}</p>}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700 flex items-start gap-2">
            <Truck className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p><strong>Shipping:</strong> Final fees determined by actual size/weight once goods arrive at our warehouse.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center border border-slate-200 rounded-lg">
              <button onClick={() => updateQty(Math.max(0, qty - 1))} className="p-2.5 hover:bg-slate-50"><Minus className="h-4 w-4" /></button>
              <span className="px-4 font-bold text-lg">{qty}</span>
              <button onClick={() => updateQty(qty + 1)} className="p-2.5 hover:bg-slate-50"><Plus className="h-4 w-4" /></button>
            </div>
            <Button onClick={() => { updateQty(qty + 1); toast.success('Added to cart!'); }} className="flex-1 h-12 bg-primary hover:bg-primary/90 font-bold text-base">
              <ShoppingCart className="h-5 w-5 mr-2" /> {qty > 0 ? 'Add More' : 'Add to Cart'}
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-2 border-t">
            <div className="flex items-center gap-1.5 text-xs text-slate-500"><Truck className="h-4 w-4 text-primary" /> Ships to Nigeria</div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500"><Shield className="h-4 w-4 text-green-500" /> Secure Pay</div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500"><Package className="h-4 w-4 text-blue-500" /> Quality Assured</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Guest cart drawer
function GuestCartDrawer({ open, onClose, guestCart, setGuestCart, exchangeRate, onCheckout }) {
  const total = guestCart.reduce((sum, i) => sum + i.price_rmb * i.qty, 0);
  const ngnTotal = exchangeRate ? (total * exchangeRate).toLocaleString(undefined, { maximumFractionDigits: 0 }) : null;

  const updateQty = (id, newQty) => {
    const updated = newQty <= 0 ? guestCart.filter(i => i._id !== id) : guestCart.map(i => i._id === id ? { ...i, qty: newQty } : i);
    setGuestCart(updated); saveGuestCart(updated);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Your Cart ({guestCart.length})</DialogTitle></DialogHeader>
        {guestCart.length === 0 ? (
          <div className="py-12 text-center"><ShoppingCart className="h-16 w-16 text-slate-200 mx-auto mb-3" /><p className="text-slate-500">Your cart is empty</p></div>
        ) : (
          <div className="space-y-4 pt-2">
            {guestCart.map(item => (
              <div key={item._id} className="flex gap-3 bg-slate-50 rounded-lg p-3">
                {item.image ? <img src={item.image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" /> : <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center"><Package className="h-6 w-6 text-slate-400" /></div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 line-clamp-1">{item.name}</p>
                  <p className="text-sm font-bold text-primary mt-0.5">¥{item.price_rmb?.toFixed(2)}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex items-center border border-slate-200 rounded bg-white">
                      <button onClick={() => updateQty(item._id, item.qty - 1)} className="p-1 hover:bg-slate-50"><Minus className="h-3 w-3" /></button>
                      <span className="px-2 text-sm font-medium">{item.qty}</span>
                      <button onClick={() => updateQty(item._id, item.qty + 1)} className="p-1 hover:bg-slate-50"><Plus className="h-3 w-3" /></button>
                    </div>
                    <button onClick={() => updateQty(item._id, 0)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <p className="text-sm font-bold text-slate-700 whitespace-nowrap">¥{(item.price_rmb * item.qty).toFixed(2)}</p>
              </div>
            ))}
            <div className="border-t pt-3 space-y-2">
              <div className="flex justify-between font-bold text-lg"><span>Total</span><span className="text-primary">¥{total.toFixed(2)}</span></div>
              {ngnTotal && <div className="flex justify-between text-sm text-slate-500"><span></span><span>≈ ₦{ngnTotal}</span></div>}
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 text-[11px] text-blue-700 flex items-start gap-1.5">
              <Truck className="h-3.5 w-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>Final shipping fees determined once goods arrive at our warehouse.</span>
            </div>
            <Button onClick={onCheckout} className="w-full h-12 bg-primary hover:bg-primary/90 font-bold text-base">
              Proceed to Checkout
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Guest checkout with embedded registration
function GuestCheckoutModal({ open, onClose, guestCart, setGuestCart, exchangeRate, onComplete }) {
  const [form, setForm] = useState({ full_name: '', email: '', password: '', phone: '' });
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState('review'); // review | register

  const total = guestCart.reduce((sum, i) => sum + i.price_rmb * i.qty, 0);
  const ngnTotal = exchangeRate ? (total * exchangeRate).toLocaleString(undefined, { maximumFractionDigits: 0 }) : null;

  const handleSubmit = async () => {
    if (!form.full_name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error('Please fill in your name, email, and password'); return;
    }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }

    setProcessing(true);
    try {
      // Payment-first: Initialize Paystack, do NOT create account yet
      const callbackUrl = `${window.location.origin}/guest-checkout-callback`;
      const res = await ecommerceAPI.guestCheckoutInit({
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        password: form.password,
        phone: form.phone.trim() || '',
        cart_items: guestCart,
        callback_url: callbackUrl
      });

      // Redirect to Paystack payment page
      if (res.data?.authorization_url) {
        // Save cart to localStorage for recovery if payment fails
        window.location.href = res.data.authorization_url;
      } else {
        toast.error('Failed to initialize payment');
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Checkout failed';
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const successNgnTotal = null; // unused, kept for compat

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-xl font-bold">{step === 'review' ? 'Order Summary' : 'Complete Your Order'}</DialogTitle></DialogHeader>
        
        {step === 'review' ? (
              <div className="space-y-4 pt-2">
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  {guestCart.map(item => (
                    <div key={item._id} className="flex justify-between text-sm">
                      <span className="text-slate-600 truncate max-w-[220px]">{item.name} × {item.qty}</span>
                      <span className="font-medium">¥{(item.price_rmb * item.qty).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 flex justify-between font-bold"><span>Total</span><span className="text-primary">¥{total.toFixed(2)}</span></div>
                  {ngnTotal && <div className="flex justify-between text-sm text-slate-500"><span></span><span>≈ ₦{ngnTotal}</span></div>}
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700 flex items-start gap-2">
                  <Truck className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p><strong>Please Note:</strong> Final shipping fees will be determined by the actual size or weight of the item depending on your chosen shipping method, which will be verified and displayed once the goods safely arrive at our warehouse.</p>
                </div>
                <Button onClick={() => setStep('register')} className="w-full h-12 bg-primary hover:bg-primary/90 font-bold">
                  Continue to Create Account & Pay
                </Button>
                <p className="text-xs text-center text-slate-500">You&apos;ll create a free account to track your order</p>
              </div>
            ) : (
              <div className="space-y-4 pt-2">
                <p className="text-sm text-slate-600">Create your account to complete this order. Your items will be saved to your dashboard.</p>
                <div className="space-y-3">
                  <div><Label className="text-sm font-medium">Full Name *</Label><Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="Your full name" className="mt-1" data-testid="checkout-name" /></div>
                  <div><Label className="text-sm font-medium">Email Address *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="you@email.com" className="mt-1" data-testid="checkout-email" /></div>
                  <div><Label className="text-sm font-medium">Password *</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Min 6 characters" className="mt-1" data-testid="checkout-password" /></div>
                  <div><Label className="text-sm font-medium">Phone Number</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Optional" className="mt-1" data-testid="checkout-phone" /></div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 flex justify-between text-sm font-bold">
                  <span>Order Total</span><span className="text-primary">¥{total.toFixed(2)} {ngnTotal && <span className="font-normal text-slate-500">(≈ ₦{ngnTotal})</span>}</span>
                </div>
                <Button onClick={handleSubmit} disabled={processing} className="w-full h-12 bg-primary hover:bg-primary/90 font-bold">
                  {processing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Redirecting to Payment...</> : 'Pay & Create Account'}
                </Button>
                <button onClick={() => setStep('review')} className="w-full text-sm text-slate-500 hover:text-slate-700">← Back to order review</button>
              </div>
            )}
      </DialogContent>
    </Dialog>
  );
}


// Trending Products Section
function TrendingProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exchangeRate, setExchangeRate] = useState(216);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const [trendRes, rateRes] = await Promise.all([
          sourcingAPI.getTrending(),
          exchangeRateAPI.getRate().catch(() => ({ data: { ngn_to_rmb: 216 } }))
        ]);
        setProducts(trendRes.data?.products || []);
        setExchangeRate(rateRes.data?.ngn_to_rmb || 216);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchTrending();
  }, []);

  if (loading) {
    return (
      <section className="py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="animate-pulse flex gap-4 overflow-hidden">
            {[1,2,3,4].map(i => (
              <div key={i} className="w-48 flex-shrink-0">
                <div className="aspect-square bg-slate-100 rounded-xl" />
                <div className="h-3 bg-slate-100 rounded mt-2 w-3/4" />
                <div className="h-4 bg-slate-100 rounded mt-1 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!products.length) return null;

  return (
    <section className="py-8 bg-white" data-testid="trending-products-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-red-500" />
            <h3 className="text-base font-bold text-slate-800">Trending on 1688</h3>
          </div>
          <span className="text-[10px] text-slate-400">Popular wholesale products</span>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          {products.map(p => {
            const ngn = exchangeRate ? Math.round(p.marked_price_rmb * exchangeRate) : null;
            return (
              <a key={p.id} href={p.source_url} target="_blank" rel="noopener noreferrer"
                className="group w-40 md:w-44 flex-shrink-0 bg-white rounded-xl border border-slate-100 hover:border-primary/30 hover:shadow-lg transition-all overflow-hidden" data-testid={`trending-${p.id}`}>
                <div className="aspect-square bg-slate-50 relative overflow-hidden">
                  {p.image ? (
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      referrerPolicy="no-referrer"
                      onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="h-8 w-8 text-slate-200" />
                    </div>
                  )}
                  {p.sales > 0 && (
                    <div className="absolute bottom-1 right-1 bg-black/50 text-white text-[8px] px-1 py-0.5 rounded">
                      {p.sales > 1000 ? `${(p.sales/1000).toFixed(1)}k` : p.sales} sold
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <p className="text-[10px] text-slate-600 line-clamp-2 min-h-[28px]">{p.title}</p>
                  <p className="text-sm font-bold text-red-600 mt-1">¥{p.marked_price_rmb?.toFixed(2)}</p>
                  {ngn && <p className="text-[9px] text-slate-400">≈ ₦{ngn.toLocaleString()}</p>}
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [clientCount, setClientCount] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/landing`)
      .then(r => r.json())
      .then(d => { if (typeof d.clients === 'number') setClientCount(d.clients); })
      .catch(() => {});
  }, []);
  const { user } = useAuth();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [rates, setRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [shopLoading, setShopLoading] = useState(true);
  const [guestCart, setGuestCart] = useState(getGuestCart);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [showProductDetail, setShowProductDetail] = useState(false);
  
  const testimonials = [
    { name: "Chidi Okonkwo", role: "Fashion Retailer", text: "SINOLINK transformed my business. I can now source directly from China with complete peace of mind.", rating: 5 },
    { name: "Amara Eze", role: "Electronics Importer", text: "The tracking system is incredible. I know exactly where my goods are at every moment.", rating: 5 },
    { name: "Tunde Bakare", role: "Wholesale Distributor", text: "Consolidation saved me over 40% on shipping costs. Highly recommended!", rating: 5 },
  ];

  // Fetch dynamic rates from backend
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await ratesAPI.getRates();
        setRates(response.data);
      } catch (error) {
        console.error('Failed to fetch rates:', error);
        // Fallback to default rates
        setRates({
          air_normal_rate: 8.5,
          air_special_rate: 9.5,
          express_rate: 12,
          sea_normal_rate: 290,
          sea_special_rate: 375,
          ngn_to_rmb: 216,
          usd_reference: 1510,
          delivery_times: {
            air_normal: "7-14 days",
            air_special: "10-14 days",
            express: "5-7 days",
            sea_normal: "30-45 days"
          }
        });
      } finally {
        setRatesLoading(false);
      }
    };
    
    fetchRates();
    // Refresh rates every 5 minutes
    const interval = setInterval(fetchRates, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch shop products and categories for full store
  useEffect(() => {
    const fetchShopData = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
          ecommerceAPI.getProducts({ limit: 24, status: 'active' }),
          ecommerceAPI.getCategories().catch(() => ({ data: { categories: [] } }))
        ]);
        const prods = prodRes.data?.products || [];
        setAllProducts(prods);
        setCategories(catRes.data?.categories || []);
      } catch (err) {
        console.error('Failed to fetch shop products:', err);
      } finally {
        setShopLoading(false);
      }
    };
    fetchShopData();
  }, []);

  // Filter products by category  
  const filteredProducts = selectedCategory
    ? allProducts.filter(p => p.category_id === selectedCategory)
    : allProducts;

  const handleViewDetail = (product) => {
    setViewProduct(product);
    setShowProductDetail(true);
  };

  const handleCheckoutComplete = (orderData) => {
    setShowCheckout(false);
    setShowCart(false);
    navigate('/order-success', { state: orderData, replace: true });
  };

  const exchangeRateValue = rates?.ngn_to_rmb || 216;
  const cartCount = guestCart.reduce((sum, i) => sum + i.qty, 0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Floating RMB Rate Widget */}
      <FloatingRateWidget rates={rates} loading={ratesLoading} />

      {/* Floating Cart Button (visible when items in cart) */}
      {cartCount > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 left-4 z-50 bg-primary text-white p-3.5 rounded-full shadow-2xl shadow-primary/40 hover:scale-110 transition-transform"
          data-testid="floating-cart-btn"
        >
          <ShoppingCart className="h-5 w-5" />
          <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{cartCount}</span>
        </button>
      )}

      {/* Product Detail Modal */}
      <ProductDetailModal product={viewProduct} open={showProductDetail} onClose={() => setShowProductDetail(false)} guestCart={guestCart} setGuestCart={setGuestCart} exchangeRate={exchangeRateValue} />
      
      {/* Guest Cart Drawer */}
      <GuestCartDrawer open={showCart} onClose={() => setShowCart(false)} guestCart={guestCart} setGuestCart={setGuestCart} exchangeRate={exchangeRateValue} onCheckout={() => { setShowCart(false); setShowCheckout(true); }} />
      
      {/* Guest Checkout Modal */}
      <GuestCheckoutModal open={showCheckout} onClose={() => setShowCheckout(false)} guestCart={guestCart} setGuestCart={setGuestCart} exchangeRate={exchangeRateValue} onComplete={handleCheckoutComplete} />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                <Ship className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl md:text-2xl font-black text-primary tracking-tight">SINOLINK</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-primary transition-colors">Features</a>
              <a href="#store" className="text-sm font-medium text-slate-600 hover:text-primary transition-colors">Shop</a>
              <a href="#how-it-works" className="text-sm font-medium text-slate-600 hover:text-primary transition-colors">How It Works</a>
              <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-primary transition-colors">Rates</a>
              <Link to="/agent/register" className="text-sm font-medium text-secondary hover:text-secondary/80 transition-colors">Become Agent</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" data-testid="login-nav-button" className="font-semibold text-sm">Login</Button>
              </Link>
              <Link to="/register">
                <Button data-testid="register-nav-button" className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-bold px-6 rounded-full shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section — Sourcing Search Engine is the centerpiece */}
      <section className="relative pt-24 pb-8 md:pt-32 md:pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-orange-50/30"></div>
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-secondary/20 to-primary/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span>
              </span>
              <span className="text-xs font-medium text-primary">Trusted by {clientCount !== null ? `${clientCount}+` : 'our'} Clients</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4 leading-[1.1]">
              Source & Ship from
              <span className="bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent"> China</span>
            </h1>
            <p className="text-base md:text-lg text-slate-500 max-w-xl mx-auto">
              Search 1688, Taobao & Pinduoduo. Order instantly — we procure, warehouse, and deliver to Nigeria.
            </p>
          </div>

          {/* Core Services — Sinolink Buy · Ship · Pay */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-6 md:p-8 mt-6" data-testid="core-services-card">
            <p className="text-[11px] font-bold text-primary uppercase tracking-widest text-center mb-6">Our 3 Core Services</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: ShoppingCart, name: 'Sinolink Buy', desc: 'We source, buy and quality-check products for you directly from 1688, Taobao & our Wholesale B2B store.', gradient: 'from-secondary/90 to-orange-600', testId: 'service-card-buy' },
                { icon: Ship, name: 'Sinolink Ship', desc: 'Air & Sea freight from China to Nigeria — warehousing, consolidation and doorstep delivery.', gradient: 'from-blue-600 to-indigo-700', testId: 'service-card-ship' },
                { icon: CreditCard, name: 'SinolinkPay', desc: 'Dual NGN & RMB wallets, supplier payments and instant Alipay transfers at great rates.', gradient: 'from-emerald-600 to-teal-700', testId: 'service-card-pay' },
              ].map(s => {
                const Icon = s.icon;
                return (
                  <Link key={s.testId} to={user ? '/dashboard' : '/register'} data-testid={s.testId}>
                    <div className={`bg-gradient-to-br ${s.gradient} rounded-2xl p-6 h-full relative overflow-hidden group hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer`}>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                      <div className="relative">
                        <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center mb-3">
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-white font-black text-lg">{s.name}</h3>
                        <p className="text-white/70 text-sm mt-2">{s.desc}</p>
                        <div className="mt-4 inline-flex items-center gap-1.5 text-white/90 text-sm font-bold group-hover:translate-x-1 transition-transform">
                          Explore <ArrowRight className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Compact Trust Stats */}
          <div className="flex justify-center gap-8 md:gap-12 mt-8">
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-black text-primary"><AnimatedCounter end={5000} suffix="+" /></p>
              <p className="text-xs text-slate-400">Orders Delivered</p>
            </div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-black text-secondary"><AnimatedCounter end={98} suffix="%" /></p>
              <p className="text-xs text-slate-400">On-Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trending Products from 1688 */}
      <TrendingProducts />

      {/* Shipping Methods Banner */}
      <section className="py-8 bg-gradient-to-r from-primary via-primary/95 to-primary overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-12 mx-8">
              <div className="flex items-center gap-3 text-white/90">
                <Plane className="h-6 w-6" />
                <span className="font-bold">Air Express</span>
              </div>
              <span className="text-white/30">●</span>
              <div className="flex items-center gap-3 text-white/90">
                <Ship className="h-6 w-6" />
                <span className="font-bold">Sea Freight</span>
              </div>
              <span className="text-white/30">●</span>
              <div className="flex items-center gap-3 text-white/90">
                <Package className="h-6 w-6" />
                <span className="font-bold">Consolidation</span>
              </div>
              <span className="text-white/30">●</span>
              <div className="flex items-center gap-3 text-white/90">
                <Warehouse className="h-6 w-6" />
                <span className="font-bold">Free Warehouse</span>
              </div>
              <span className="text-white/30">●</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <RevealSection>
      <section id="features" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-secondary/10 text-secondary font-bold text-sm rounded-full mb-4">
              POWERFUL FEATURES
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4">
              Everything You Need to Import
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From finding products to receiving them at your door — we&apos;ve got you covered
            </p>
          </div>

          <AutoCarousel itemClassName="basis-[85%] md:basis-1/2 lg:basis-1/3" interval={4500} testId="features-carousel">
            {/* Feature 1 */}
            <div className="group relative bg-gradient-to-br from-slate-50 to-white p-8 rounded-3xl border border-slate-100 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/10 transition-all duration-500 h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-secondary/10 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-secondary to-orange-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-secondary/30 group-hover:scale-110 transition-transform">
                  <Globe className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Source Anywhere</h3>
                <p className="text-slate-600">Buy from 1688, Taobao, Alibaba, PDD and more. We procure on your behalf with the best rates.</p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="group relative bg-gradient-to-br from-slate-50 to-white p-8 rounded-3xl border border-slate-100 hover:border-secondary/30 hover:shadow-2xl hover:shadow-secondary/10 transition-all duration-500 h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/70 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform">
                  <Warehouse className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Free Warehousing</h3>
                <p className="text-slate-600">Store your goods for up to 30 days free. Consolidate multiple packages to save on shipping.</p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="group relative bg-gradient-to-br from-slate-50 to-white p-8 rounded-3xl border border-slate-100 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/10 transition-all duration-500 h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Secure Payments</h3>
                <p className="text-slate-600">Pay with Paystack, bank transfer or wallet. Your money is safe until goods are delivered.</p>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="group relative bg-gradient-to-br from-slate-50 to-white p-8 rounded-3xl border border-slate-100 hover:border-secondary/30 hover:shadow-2xl hover:shadow-secondary/10 transition-all duration-500 h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Real-Time Tracking</h3>
                <p className="text-slate-600">Track every step from purchase to delivery. Get notifications at every milestone.</p>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="group relative bg-gradient-to-br from-slate-50 to-white p-8 rounded-3xl border border-slate-100 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/10 transition-all duration-500 h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Save Up to 40%</h3>
                <p className="text-slate-600">Consolidate multiple orders into one shipment. The more you ship, the more you save.</p>
              </div>
            </div>

            {/* Feature 6 */}
            <div className="group relative bg-gradient-to-br from-slate-50 to-white p-8 rounded-3xl border border-slate-100 hover:border-secondary/30 hover:shadow-2xl hover:shadow-secondary/10 transition-all duration-500 h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-secondary/10 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-secondary to-orange-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-secondary/30 group-hover:scale-110 transition-transform">
                  <HeadphonesIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">24/7 Support</h3>
                <p className="text-slate-600">Our team is always available via WhatsApp. Get help anytime you need it.</p>
              </div>
            </div>
          </AutoCarousel>
        </div>
      </section>
      </RevealSection>

      {/* How It Works */}
      <RevealSection>
      <section id="how-it-works" className="py-20 md:py-32 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary font-bold text-sm rounded-full mb-4">
              SIMPLE PROCESS
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Four simple steps to get your goods from China to Nigeria
            </p>
          </div>

          <AutoCarousel itemClassName="basis-[85%] md:basis-1/2 lg:basis-1/3 pt-6 pr-2" interval={4000} testId="how-it-works-carousel">
            {[
              { step: 1, title: "Register & Fund", desc: "Create account and fund your wallet", icon: CreditCard, color: "from-secondary to-orange-400" },
              { step: 2, title: "Submit Order", desc: "Add product links or shop directly", icon: Package, color: "from-primary to-primary/70" },
              { step: 3, title: "Consolidate", desc: "Combine packages at warehouse", icon: Warehouse, color: "from-purple-500 to-purple-400" },
              { step: 4, title: "Receive", desc: "Track & receive in Nigeria", icon: Truck, color: "from-green-500 to-green-400" },
            ].map((item) => (
              <div key={item.step} className="relative group h-full">
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-500 relative z-10 h-full">
                  <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                    <item.icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute -top-4 right-2 w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center text-white font-black text-lg shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </AutoCarousel>
        </div>
      </section>
      </RevealSection>

      {/* ═══════════ FULL EMBEDDED E-COMMERCE STORE ═══════════ */}
      <RevealSection>
      <section id="store" className="py-16 md:py-24 bg-gradient-to-b from-white via-slate-50/50 to-white" data-testid="landing-store-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Store Header */}
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary font-bold text-sm rounded-full mb-4">SHOP DIRECTLY</span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-3">Order with Sinolink</h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">Quality products sourced from China — delivered to your door in Nigeria</p>
          </div>

          {/* Store Navigation Bar */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 md:p-4 mb-8">
            <div className="flex items-center justify-between gap-3">
              {/* Category Filter Pills */}
              <div className="flex-1 overflow-x-auto scrollbar-hide">
                <div className="flex gap-2 pb-0.5">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${!selectedCategory ? 'bg-primary text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-primary/10 hover:text-primary'}`}
                  >All Products</button>
                  {categories.map(cat => (
                    <button
                      key={cat._id}
                      onClick={() => setSelectedCategory(cat._id)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${selectedCategory === cat._id ? 'bg-primary text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-primary/10 hover:text-primary'}`}
                    >{cat.icon || ''} {cat.name}</button>
                  ))}
                </div>
              </div>
              {/* Cart Button */}
              <button
                onClick={() => setShowCart(true)}
                className="relative flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-full font-semibold text-sm hover:bg-primary/90 transition-all shadow-md flex-shrink-0"
                data-testid="guest-cart-btn"
              >
                <ShoppingCart className="h-4 w-4" />
                <span className="hidden sm:inline">Cart</span>
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{cartCount}</span>
                )}
              </button>
            </div>
          </div>

          {/* Product Grid */}
          {shopLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className="animate-pulse bg-white rounded-xl border border-slate-100 overflow-hidden">
                  <div className="aspect-square bg-slate-200" />
                  <div className="p-3 space-y-2"><div className="h-4 bg-slate-200 rounded w-3/4" /><div className="h-5 bg-slate-200 rounded w-1/2" /></div>
                </div>
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filteredProducts.map(product => (
                <StoreProductCard
                  key={product._id}
                  product={product}
                  guestCart={guestCart}
                  setGuestCart={setGuestCart}
                  onViewDetail={handleViewDetail}
                  exchangeRate={exchangeRateValue}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4"><Store className="h-10 w-10 text-primary" /></div>
              <h4 className="font-bold text-lg text-slate-800 mb-2">{selectedCategory ? 'No products in this category' : 'Store Opening Soon'}</h4>
              <p className="text-sm text-slate-500">{selectedCategory ? 'Try selecting a different category' : 'Amazing products coming your way!'}</p>
              {selectedCategory && <button onClick={() => setSelectedCategory(null)} className="mt-3 text-primary font-semibold text-sm hover:underline">View All Products</button>}
            </div>
          )}

          {/* Store footer info strip */}
          {filteredProducts.length > 0 && (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center"><Shield className="h-4 w-4 text-green-600" /></div>
                <div><p className="font-semibold text-sm text-slate-800">Secure Payment</p><p className="text-[11px] text-slate-500">100% Protected</p></div>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center"><Truck className="h-4 w-4 text-blue-600" /></div>
                <div><p className="font-semibold text-sm text-slate-800">Fast Delivery</p><p className="text-[11px] text-slate-500">China → Nigeria</p></div>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center"><Star className="h-4 w-4 text-orange-600" /></div>
                <div><p className="font-semibold text-sm text-slate-800">Quality Products</p><p className="text-[11px] text-slate-500">Verified Suppliers</p></div>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center"><Clock className="h-4 w-4 text-purple-600" /></div>
                <div><p className="font-semibold text-sm text-slate-800">24/7 Support</p><p className="text-[11px] text-slate-500">Always Here</p></div>
              </div>
            </div>
          )}
        </div>
      </section>
      </RevealSection>

      {/* Testimonials */}
      <RevealSection>
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary via-primary to-primary/90 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-white/20 text-white font-bold text-sm rounded-full mb-4">
              TESTIMONIALS
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4">
              Loved by Importers
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 md:p-12 border border-white/20">
              <div className="flex gap-1 mb-6">
                {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-xl md:text-2xl text-white mb-8 leading-relaxed">
                &ldquo;{testimonials[activeTestimonial].text}&rdquo;
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-white text-lg">{testimonials[activeTestimonial].name}</p>
                  <p className="text-white/60">{testimonials[activeTestimonial].role}</p>
                </div>
                <div className="flex gap-2">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveTestimonial(i)}
                      className={`w-3 h-3 rounded-full transition-all ${i === activeTestimonial ? 'bg-secondary w-8' : 'bg-white/30'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </RevealSection>

      {/* Pricing/Rates Section */}
      <RevealSection>
      <section id="pricing" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-secondary/10 text-secondary font-bold text-sm rounded-full mb-4">
              TRANSPARENT PRICING
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4">
              Competitive Shipping Rates
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              No hidden fees. Pay only for what you ship. Rates updated in real-time.
            </p>
            {rates?.updated_at && (
              <p className="text-sm text-slate-400 mt-2 flex items-center justify-center gap-2">
                <RefreshCw className="h-3 w-3" />
                Last updated: {new Date(rates.updated_at).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* RMB Exchange Rate Banner */}
          <div className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-2xl p-6 mb-12 border border-primary/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center shadow-lg shadow-primary/30">
                  <TrendingUp className="h-7 w-7 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Current Exchange Rate</p>
                  <p className="text-3xl font-black text-primary">
                    ₦{rates?.ngn_to_rmb || 216} <span className="text-lg font-medium text-slate-500">per ¥1 RMB</span>
                  </p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
                <div className="text-center">
                  <p className="text-xs text-slate-500">¥100 RMB</p>
                  <p className="text-xl font-bold text-primary">₦{((rates?.ngn_to_rmb || 216) * 100).toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500">¥1,000 RMB</p>
                  <p className="text-xl font-bold text-primary">₦{((rates?.ngn_to_rmb || 216) * 1000).toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500">USD Reference</p>
                  <p className="text-xl font-bold text-secondary">₦{rates?.usd_reference || 1510}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Live Rates — single box */}
          <div className="max-w-4xl mx-auto bg-white rounded-3xl border-2 border-slate-200 shadow-xl p-6 md:p-8" data-testid="live-rates-box">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-black text-lg text-slate-800">Live Shipping Rates</h3>
              <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                LIVE
              </span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {[
                { icon: Plane, name: 'Air Normal', rate: rates?.air_normal_rate ?? 8.5, unit: '/kg', time: rates?.delivery_times?.air_normal || '7-14 days', color: 'text-primary', bg: 'bg-primary/10', badge: 'POPULAR' },
                { icon: Zap, name: 'Express Air', rate: rates?.express_rate ?? rates?.air_special_rate ?? 12, unit: '/kg', time: rates?.delivery_times?.express || rates?.delivery_times?.air_special || '3-7 days', color: 'text-secondary', bg: 'bg-secondary/10' },
                { icon: Ship, name: 'Sea Normal', rate: rates?.sea_normal_rate ?? 290, unit: '/CBM', time: rates?.delivery_times?.sea_normal || '60-75 days', color: 'text-blue-600', bg: 'bg-blue-50' },
                { icon: Ship, name: 'Sea Special', rate: rates?.sea_special_rate ?? 375, unit: '/CBM', time: rates?.delivery_times?.sea_special || '60-75 days', color: 'text-indigo-600', bg: 'bg-indigo-50' },
              ].map(r => {
                const Icon = r.icon;
                return (
                  <div key={r.name} className="relative rounded-2xl border border-slate-200 p-4 hover:border-primary hover:shadow-md transition-all text-center" data-testid={`rate-tile-${r.name.replace(/\s+/g, '-').toLowerCase()}`}>
                    {r.badge && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-white px-2.5 py-0.5 rounded-full text-[9px] font-bold">{r.badge}</span>}
                    <div className={`w-10 h-10 ${r.bg} rounded-xl flex items-center justify-center mx-auto mb-2`}>
                      <Icon className={`h-5 w-5 ${r.color}`} />
                    </div>
                    <p className="font-bold text-sm text-slate-800">{r.name}</p>
                    <p className={`text-xl font-black ${r.color} mt-1`}>${r.rate}<span className="text-xs text-slate-400 font-semibold">{r.unit}</span></p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{r.time}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/login">
              <Button variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-white font-bold px-8 h-12 rounded-full">
                View All Rates <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </RevealSection>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-4xl md:text-6xl font-black text-white mb-8">
            Ready<span className="bg-gradient-to-r from-secondary to-orange-400 bg-clip-text text-transparent">?</span>
          </h2>
          <div className="flex justify-center">
            <Link to="/register">
              <Button data-testid="footer-cta-button" className="h-16 px-12 text-lg font-bold bg-gradient-to-r from-secondary to-orange-500 hover:from-secondary/90 hover:to-orange-500/90 text-white rounded-full shadow-2xl shadow-secondary/30 transition-all transform hover:scale-105">
                Ready? Create your account <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Agent Section */}
      <section className="py-16 bg-gradient-to-r from-secondary/10 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Want to Earn Commission?
              </h3>
              <p className="text-slate-600">
                Become an agent and earn on every order your customers make.
              </p>
            </div>
            <Link to="/agent/register">
              <Button className="bg-secondary hover:bg-secondary/90 text-white font-bold px-8 h-14 rounded-full shadow-lg shadow-secondary/30">
                <Users className="mr-2 h-5 w-5" /> Apply as Agent
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                  <Ship className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-black">SINOLINK</span>
              </div>
              <p className="text-slate-400 text-sm">
                Your trusted partner for China to Nigeria logistics.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/register" className="hover:text-white transition-colors">Get Started</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Login</Link></li>
                <li><Link to="/rates" className="hover:text-white transition-colors">Rates</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Procurement</li>
                <li>Warehousing</li>
                <li>Consolidation</li>
                <li>Shipping</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/guide" className="hover:text-white transition-colors">User Guide</Link></li>
                <li><Link to="/training" className="hover:text-white transition-colors">Training</Link></li>
                <li><Link to="/support" className="hover:text-white transition-colors">Contact Us</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
            © {new Date().getFullYear()} SINOLINK. All rights reserved.
          </div>
        </div>
      </footer>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes fade-in {
          0% { opacity: 0; transform: translateY(-10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes widget-glow {
          0%, 100% { box-shadow: 0 8px 32px rgba(139, 10, 58, 0.25); }
          50% { box-shadow: 0 8px 48px rgba(139, 10, 58, 0.45); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
        .animate-marquee { animation: marquee 20s linear infinite; }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        .landing-rate-widget > div { animation: widget-glow 3s ease-in-out infinite; }
        
        /* Scroll reveal animations */
        .scroll-reveal {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal.revealed {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}
